import { useEffect, useState } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Video · DIRECTRIZ-VIDEO F0 / AC-20 (4)
   =====================================================================
   Sin cámaras no hay motor, y la pantalla lo dice en vez de pintar
   verde. Con eventos: lo que las cámaras contaron (qué, dónde, cuándo;
   nunca quién) y el cruce con la presencia. Ver esto es un acto con
   permiso y queda en bitácora.
   ===================================================================== */
const TIPOS = { PERSONA_ENTRA: 'Entró alguien', PERSONA_SALE: 'Salió alguien', OBJETO_TOMADO: 'Se tomó un bulto', OBJETO_DEVUELTO: 'Se devolvió un bulto' };

export default function Video() {
  const [estado, setEstado] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [cruce, setCruce] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let vivo = true;
    Promise.all([api.get('/video/estado'), api.get('/video/eventos?horas=24'), api.get('/video/cruce')])
      .then(([e, ev, c]) => { if (vivo) { setEstado(e); setEventos(ev); setCruce(c); setError(''); } })
      .catch((err) => { if (vivo) setError(err.message); });
    return () => { vivo = false; };
  }, []);
  if (error && !estado) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!estado) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Preguntando por las cámaras…
      </p>
    );
  }
  return (
    <div className="flex max-w-4xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Video</h1>
        <p className="text-gray-600">Lo que las cámaras le cuentan a TEGO. Qué pasa; nunca quién.</p>
        <Ayuda id="video.pantalla" />
      </header>

      <section className={`rounded-xl border p-4 ${!estado.hay_motor ? 'border-gray-300 bg-gray-50' : estado.callado ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
        <h2 className="flex items-center gap-2 font-semibold text-gray-900"><Camera className="h-4 w-4" aria-hidden="true" /> Motor de video · {!estado.hay_motor ? 'no hay' : estado.callado ? 'callado' : 'hablando'}</h2>
        <p className="text-sm text-gray-800">{estado.dice}</p>
        <Ayuda id="video.sin_motor" />
        <p className="mt-1 text-xs text-gray-500">Canal: {estado.puente?.topico} · broker {estado.puente?.broker_configurado ? 'configurado' : 'sin configurar'} · puente {estado.puente?.enganchado ? 'enganchado' : 'sin enganchar'}</p>
      </section>

      <section className="rounded-xl border border-gray-200 p-4">
        <h2 className="font-semibold text-gray-900">Últimas 24 horas</h2>
        {eventos.length === 0 ? (
          <NotaExplicativa titulo="Ningún evento">Cuando una cámara vea entrar o salir a alguien, o tomar un bulto, aparece aquí con su hora y su zona.</NotaExplicativa>
        ) : (
          <ul className="mt-2 divide-y divide-gray-100 text-sm">{eventos.map((e) => (
            <li key={e.id} className="flex flex-wrap gap-x-3 py-1">
              <span className="text-gray-500">{String(e.hora).slice(0, 16).replace('T', ' ')}</span>
              <span className="font-medium">{TIPOS[e.tipo] || e.tipo}</span>
              <span className="font-mono">{e.ubicacion || '— sin ubicación de TEGO —'}</span>
              <span className="text-gray-500">cámara {e.id_camara}</span>
              {e.confianza != null && <span className="text-xs text-gray-500">{Math.round(e.confianza * 100)} %</span>}
            </li>))}</ul>
        )}
      </section>

      {cruce && (
        <section className="rounded-xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900">Cruce con la presencia (último día)</h2>
          <Ayuda id="video.cruce" />
          <p className="text-sm text-gray-700">{cruce.dice}</p>
          {cruce.presencia.filter((p) => !p.ficho).length > 0 && (
            <ul className="mt-2 text-sm text-amber-800">{cruce.presencia.filter((p) => !p.ficho).map((p, i) => (
              <li key={i}>{String(p.hora_video).slice(11, 16)} · cámara {p.id_camara}: entró alguien y nadie fichó en ±{cruce.ventana_segundos} s</li>))}</ul>
          )}
          {cruce.kardex.length > 0 && (
            <ul className="mt-2 text-sm text-gray-700">{cruce.kardex.map((k) => (
              <li key={k.id_ubicacion}>Ubicación {k.id_ubicacion}: {k.salidas_vistas} salida(s) vistas, {k.salidas_apuntadas} apuntadas</li>))}</ul>
          )}
        </section>
      )}
    </div>
  );
}
